import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Loader2 } from "lucide-react";

interface GeneratedPost {
  title: string;
  slug: string;
  excerpt: string;
  content_html: string;
  seo_keywords: string;
  image_prompts: string[];
}

interface GeneratePostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerated: (post: GeneratedPost) => void;
}

const GeneratePostModal = ({ open, onOpenChange, onGenerated }: GeneratePostModalProps) => {
  const { toast } = useToast();
  const [keyword, setKeyword] = useState("");
  const [tone, setTone] = useState("pastoral");
  const [articleLength, setArticleLength] = useState("medio");
  const [targetAudience, setTargetAudience] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!keyword.trim()) {
      toast({ title: "Informe uma palavra-chave", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-blog-post", {
        body: { keyword, tone, articleLength, targetAudience: targetAudience || undefined },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      onGenerated(data as GeneratedPost);
      onOpenChange(false);
      toast({ title: "Post gerado com sucesso!" });
    } catch (e) {
      toast({ title: "Erro ao gerar", description: e instanceof Error ? e.message : "Tente novamente.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Gerar Post com IA
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Palavra-chave / Tema *</Label>
            <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Ex: O poder da oração" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Tom</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pastoral">Pastoral</SelectItem>
                  <SelectItem value="educativo">Educativo</SelectItem>
                  <SelectItem value="jovem">Jovem</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Tamanho</Label>
              <Select value={articleLength} onValueChange={setArticleLength}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="curto">Curto</SelectItem>
                  <SelectItem value="medio">Médio</SelectItem>
                  <SelectItem value="longo">Longo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Público-alvo (opcional)</Label>
            <Input value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)} placeholder="Ex: jovens, líderes, famílias" />
          </div>
          <Button onClick={handleGenerate} disabled={loading} className="w-full">
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Gerando...</>
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Gerar artigo</>
            )}
          </Button>
          {loading && <p className="text-xs text-center text-muted-foreground">Isso pode levar até 30 segundos...</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GeneratePostModal;
